import type PptxGenJS from '@meitu/js-to-pptx';
import { createCanvas as createCanvasWasm } from '@meitu/wasm-lib';
import { pxToInch } from '@/utils/unit-kit';
import type { IImageLayerProps, ILayerProps, ISlide } from '@/version-types';
import { getImageEffectResultImgSize } from '../plugins/image-effect/runtime/constants';
import { getImageLayerEffectPosition } from './render-to-canvas';
import { renderLayerToStage } from './render-to-stage';

export const renderLayerToPPT = async (
    slide: ISlide,
    layer: ILayerProps,
    stageWidth: number,
    stageHeight: number,
    identity: string,
) => {
    const { left, top, width, height, rotate, data } = layer;
    const options: PptxGenJS.ImageProps = {
        x: pxToInch(left),
        y: pxToInch(top),
        w: pxToInch(width),
        h: pxToInch(height),
        rotate: rotate > 0 ? rotate : 360 + rotate,
        data: '',
    };

    const canvas = await renderLayerToStage(
        {
            ...layer,
            rotate: 0,
        },
        {
            width: stageWidth,
            height: stageHeight,
        },
        identity,
    );

    if (!canvas) {
        return;
    }

    const { effect } = data as IImageLayerProps;
    if (effect?.screenshotUrl) {
        const imgEffectResultImgSize = getImageEffectResultImgSize(data as IImageLayerProps);
        let [x, y, w, h] = [
            left - (width * imgEffectResultImgSize - width) / 2,
            top - (height * imgEffectResultImgSize - height) / 2,
            width * imgEffectResultImgSize,
            height * imgEffectResultImgSize,
        ];
        const effectPosition = getImageLayerEffectPosition(layer);
        if (effectPosition) {
            x = left + effectPosition.left;
            y = top + effectPosition.top;
            w = effectPosition.right - effectPosition.left;
            h = effectPosition.bottom - effectPosition.top;
        }

        const resultCanvas = createCanvasWasm(w, h);
        const ctx = resultCanvas.getContext('2d');
        if (ctx) {
            ctx.globalAlpha = layer.opacity;
        }
        ctx?.drawImage(canvas, x, y, w, h, 0, 0, w, h);
        options.x = pxToInch(x);
        options.y = pxToInch(y);
        options.w = pxToInch(w);
        options.h = pxToInch(h);
        options.data = resultCanvas?.toDataURL() || '';
    } else {
        const resultCanvas = createCanvasWasm(width, height);
        const ctx = resultCanvas.getContext('2d');
        ctx?.drawImage(canvas, left, top, width, height, 0, 0, width, height);
        options.data = resultCanvas?.toDataURL() || '';
    }

    slide.addImage(options);
};
